/**
 * MEETING EDIT FORM BODY — Form di modifica inline dei campi di una Meeting.
 *
 * Embed dentro AccordionDetails di ActivityTimeline. Pattern coerente
 * con TaskEditFormBody/NoteEditFormBody/CallEditFormBody.
 */
import { useTranslation } from 'react-i18next';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { RichTextField } from '../../components/RichTextField';
import { useAppDispatch } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { updateMeeting } from './meetingThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { ActivityTools } from '../../components/ActivityTools';
import { MEETING_STATUSES, type Meeting, type MeetingCreatePayload, type MeetingUpdatePayload } from './meetingTypes';

interface MeetingEditFormBodyProps {
  meeting: Meeting;
}

export function MeetingEditFormBody({ meeting }: MeetingEditFormBodyProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();

  const meetingFqn = getEntityFqn('Meeting');
  const {
    records: currentlyLinked,
    onRecordsChange: handleRecordsChange,
    recordsSaving,
    pickerProps: recordsPickerProps,
  } = useAssociatedRecordsBinder({
    mode: 'edit',
    entityFqn: meetingFqn ?? '',
    entityId: meeting.meetingId,
  });

  const commitField =
    (field: keyof MeetingCreatePayload) =>
    async (newValue: EditableValue) => {
      try {
        const payload = { meetingId: meeting.meetingId, [field]: newValue } as MeetingUpdatePayload;
        await dispatch(updateMeeting(payload)).unwrap();
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    };

  return (
    <div className="meeting-edit-body">
      <div className="task-edit-body__grid task-edit-body__grid--2col">
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">{t('meeting.meetingDate')}</div>
          <EditableField
            value={meeting.meetingDate}
            variant={{ kind: 'datetime' }}
            required
            onCommit={commitField('meetingDate')}
            label={t('meeting.meetingDate')}
          />
        </div>
        <div className="field-row">
          <div className="field-row__label text-xs text-muted">
            {`${t('meeting.duration')} (${t('meeting.durationMinutes')})`}
          </div>
          <EditableField
            value={String(meeting.duration)}
            variant={{ kind: 'text' }}
            required
            onCommit={(v) => commitField('duration')(Number(v) || 1)}
            label={t('meeting.duration')}
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('meeting.status')}</div>
        <EditableField
          value={meeting.status}
          variant={{ kind: 'select', options: MEETING_STATUSES }}
          required
          onCommit={commitField('status')}
          label={t('meeting.status')}
        />
      </div>

      <div className="field-row">
        <div className="field-row__label text-xs text-muted">{t('meeting.description')}</div>
        <RichTextField
          value={meeting.description}
          onCommit={commitField('description')}
          placeholder={t('meeting.descriptionPlaceholder')}
          required
        />
      </div>

      <ActivityTools
        {...recordsPickerProps}
        records={currentlyLinked}
        onChange={(val) => handleRecordsChange?.(val)}
        recordsSaving={recordsSaving}
        entityFqn={meetingFqn ?? ''}
        entityId={meeting.meetingId}
      />
    </div>
  );
}
